import { computed, onScopeDispose, shallowRef, toValue, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { apiRequest } from '@/api/http';
import { parseRoleIds, RoleCode } from '@/constants/accounts';
import { useModuleOptions } from '@/composables/useModuleOptions';
import { requestPageRefresh } from '@/composables/usePageRefresh';

// The live stream (below) brings new notifications at once; polling is the
// fallback for when it's down.
const POLL_MS = 3 * 60 * 1000;
// Waits before reconnecting a dropped stream: doubles from 2s up to 1min.
const RECONNECT_MIN_MS = 2 * 1000;
const RECONNECT_MAX_MS = 60 * 1000;
// Coming back to the tab refreshes the bell, but not more often than this.
const FOCUS_REFRESH_MS = 30 * 1000;
// Sent by a page's refresh button so the bell reloads along with the page.
const REFRESH_EVENT = 'app:notifications-refresh';

// Reloads the bell now: for a refresh the user asked for, which shouldn't
// leave the notifications as they were.
export function refreshNotifications() {
  window.dispatchEvent(new CustomEvent(REFRESH_EVENT));
}

// LARDO remarks hold the warning and the recommendation in one string:
// "… Recommendation: …".
const earlyWarning = (remarks) =>
  String(remarks || '').replace(/Recommendation:\s*.*$/is, '').trim();
const recommendationOf = (remarks) =>
  String(remarks || '').match(/Recommendation:\s*(.*)$/is)?.[1]?.trim() || '';

// Attendance alerts (notification.type) in the parent's feed: each absent
// or late mark, and the LARDO early warnings their child's adviser gets.
const PARENT_ALERTS = {
  absent: { badge: 'Absent', badgeVariant: 'red', icon: 'mdi-calendar-remove-outline' },
  late: { badge: 'Late', badgeVariant: 'amber', icon: 'mdi-clock-alert-outline' },
  lardo: { badge: 'Early warning', badgeVariant: 'red', icon: 'mdi-file-document-alert-outline' },
};

const isRead = (row) => row.read === true || Number(row.read) === 1;

// Each feed: how to show an entry, where opening it goes (relative to the
// role's base path) and, optionally, the role that page is for. The server
// only ever returns the signed-in user's own entries: GET
// /notification/feed/<name>, PATCH …/<id>/read, POST …/read-all.
// Icons for the "Updates" feed, by notification type.
const UPDATE_ICONS = {
  account_pending: 'mdi-account-clock-outline',
  parent_signup: 'mdi-account-child-outline',
  account_approved: 'mdi-account-check-outline',
  announcement: 'mdi-bullhorn-outline',
  school_event: 'mdi-calendar-star',
  discipline_report: 'mdi-alert-octagon-outline',
  discipline_update: 'mdi-clipboard-text-outline',
  appointment: 'mdi-calendar-account-outline',
  grades_submitted: 'mdi-clipboard-check-outline',
  grades_released: 'mdi-file-certificate-outline',
  grades_complete: 'mdi-check-all',
};

// A JHS/SHS Admin's LARDO and at-risk alerts come as Updates, but are listed
// under the LARDO and At-risk tabs like everyone else's.
const UPDATE_CATEGORY = { lardo_alert: 'lardo', at_risk_alert: 'risk' };
const ADMIN_ALERT_ROLES = [RoleCode.JhsAdmin, RoleCode.ShsAdmin];

const FEEDS = {
  // Sign-ups, announcements, events, discipline reports, appointments, and
  // the admins' LARDO and at-risk alerts (all roles).
  updates: {
    map: (row) =>
      row.type === 'lardo_alert'
        ? {
            category: 'lardo',
            title: row.title,
            text: earlyWarning(row.body),
            recommendation: recommendationOf(row.body),
          }
        : {
            category: UPDATE_CATEGORY[row.type] || 'updates',
            icon: UPDATE_ICONS[row.type] || (UPDATE_CATEGORY[row.type] ? '' : 'mdi-bell-outline'),
            title: row.title,
            text: row.body || '',
          },
    // The page the server chose ('' = home; null = nothing to open). Old
    // rows may still use the /parent-children address.
    target: (row) => (row.route == null ? null : row.route.replace(/^\/parent-children/, '/my-children')),
    // Set by the server when the page differs by role (e.g. Discipline for a
    // teacher vs. the Prefect). Older rows have none.
    role: (row) => row.role || null,
  },
  lardoAdviser: {
    map: (row) => ({
      category: 'lardo',
      text: earlyWarning(row.remarks),
      recommendation: recommendationOf(row.remarks),
    }),
    // The adviser's class list (the stored route comes from a database
    // trigger and isn't reliable).
    target: () => '/advisory-rooms',
    role: () => RoleCode.Adviser,
  },
  lardoFaculty: {
    map: (row) => ({
      category: 'lardo',
      text: earlyWarning(row.remarks),
      recommendation: recommendationOf(row.remarks),
    }),
    // A subject teacher's class records, where the scores are.
    target: () => '/students-records',
    role: () => RoleCode.Teacher,
  },
  riskAdviser: {
    // A record whose grade is back to 80 or above stays, marked recovered.
    map: (row) => ({
      category: 'risk',
      text: row.remarks,
      recommendation: row.recommendation,
      ...(row.status === 'resolved' ? { badge: 'Recovered', badgeVariant: 'green' } : {}),
    }),
    target: () => '/advisory-rooms',
    role: () => RoleCode.Adviser,
  },
  riskFaculty: {
    map: (row) => ({ category: 'risk', text: row.remarks, recommendation: row.recommendation }),
    target: () => '/students-records',
    role: () => RoleCode.Teacher,
  },
  parent: {
    // Grade alerts, and attendance alerts (row.type).
    map: (row) =>
      PARENT_ALERTS[row.type]
        ? {
            category: 'parent',
            ...PARENT_ALERTS[row.type],
            text: row.remarks || '',
            meta: row.quarter || '',
          }
        : {
            category: 'parent',
            text: row.remarks ? `Remarks: ${row.remarks}` : '',
            meta: Number(row.transmuted_grade) ? `Final grade: ${row.transmuted_grade}` : '',
          },
    // That child on My Children: attendance alerts open their attendance,
    // grade alerts their report card.
    target: (row) =>
      row.studentID
        ? `/my-children?${PARENT_ALERTS[row.type] && row.type !== 'lardo' ? 'attendance' : 'report'}=${row.studentID}`
        : '',
  },
};

// Feeds for all of the user's roles (not only the one in use), so a teacher
// who is also an adviser sees both kinds of alerts. Everyone gets Updates.
function feedsFor(roleCodes) {
  const feeds = new Set(['updates']);
  if (roleCodes.includes(RoleCode.Adviser)) ['lardoAdviser', 'riskAdviser'].forEach((f) => feeds.add(f));
  if (roleCodes.includes(RoleCode.Teacher)) ['lardoFaculty', 'riskFaculty'].forEach((f) => feeds.add(f));
  if (roleCodes.includes(RoleCode.Parent)) feeds.add('parent');
  return [...feeds];
}

// Tabs, in this order, for the kinds of notifications the user gets (shown
// when there's more than one).
const TABS = [
  { value: 'updates', label: 'Updates', empty: 'No updates', hint: 'New announcements and activity will show up here.' },
  { value: 'lardo', label: 'LARDO', empty: 'No LARDO alerts', hint: 'New alerts will show up here.' },
  { value: 'risk', label: 'At-risk', empty: 'No at-risk alerts', hint: 'Nothing needs your attention right now.' },
  { value: 'parent', label: 'My children', empty: 'No alerts', hint: "We'll let you know about grades, absences, lates and reports." },
];
const FEED_CATEGORY = {
  updates: 'updates',
  lardoAdviser: 'lardo',
  lardoFaculty: 'lardo',
  riskAdviser: 'risk',
  riskFaculty: 'risk',
  parent: 'parent',
};

const feedUrl = (feed, path = '') => `/notification/feed/${feed}${path}`;

// The pages in a role's menu (its assign_mods), e.g. ['/dashboard', '/accounts'].
function menuPages(role) {
  let links = [];
  try {
    links = JSON.parse(role?.assign_mods || '[]');
  } catch {
    links = [];
  }
  return (Array.isArray(links) ? links : [])
    .flatMap((link) => [link.route, ...(link.subLink || []).map((sub) => sub.route)])
    .filter(Boolean);
}

// Class Adviser includes Subject Teacher, so it covers the teacher's pages.
const covers = (code, wanted) => code === wanted || (wanted === RoleCode.Teacher && code === RoleCode.Adviser);

// Listens to GET /notification/stream (server-sent events) and calls
// onNotification for each new notification. Read with fetch rather than
// EventSource, which can't send the Authorization header (and the token
// mustn't go in the URL). Reconnects when the connection drops; returns a
// function that closes it for good.
function openLiveStream(onNotification) {
  let controller = null;
  let retryTimer = null;
  let delay = RECONNECT_MIN_MS;
  let closed = false;

  const retry = () => {
    if (closed) return;
    retryTimer = setTimeout(connect, delay);
    delay = Math.min(delay * 2, RECONNECT_MAX_MS);
  };

  async function connect() {
    const token = localStorage.getItem('token');
    if (closed || !token) return;
    controller = new AbortController();
    try {
      const res = await fetch(`${process.env.VUE_APP_SERVER}/notification/stream`, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
        signal: controller.signal,
      });
      // Signed out or refused: polling carries on; don't keep knocking.
      if (res.status === 401 || res.status === 403) return;
      if (!res.ok || !res.body) throw new Error(`stream ${res.status}`);
      delay = RECONNECT_MIN_MS;
      const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
      let buffer = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += value;
        // Events are separated by a blank line; only 'notification' matters
        // (the others are heartbeats).
        const events = buffer.split(/\r?\n\r?\n/);
        buffer = events.pop();
        if (events.some((e) => /^event: ?notification$/m.test(e))) onNotification();
      }
    } catch {
      if (closed) return;
    }
    retry();
  }

  connect();
  return () => {
    closed = true;
    clearTimeout(retryTimer);
    controller?.abort();
  };
}

// The signed-in user's notifications for all their roles and the selected
// school year: refreshed as soon as a new one arrives, every few minutes as
// a fallback, and when the tab is opened again.
// Options are refs/getters: userId, roleCode, basePath.
export function useNotifications({ userId, roleCode, basePath }) {
  const router = useRouter();
  const store = useStore();
  const items = shallowRef([]);
  const loading = shallowRef(false);
  let loadedAt = 0;
  let loadedOnce = false;

  const { roles, findRoleCode } = useModuleOptions();
  const schoolYearId = () => store.getters.getFilterSelected || null;
  const yearQuery = () => (schoolYearId() ? `?schoolYearId=${schoolYearId()}` : '');
  const ctx = () => ({ userId: toValue(userId), roleCode: toValue(roleCode) });
  // Every role granted to the user, plus the one in use.
  const roleCodes = computed(() => {
    const granted = parseRoleIds(store.state.user?.user?.subModules).map(findRoleCode);
    return [...new Set([...granted, ctx().roleCode].filter(Boolean))];
  });
  const feeds = computed(() => feedsFor(roleCodes.value));
  const tabs = computed(() => {
    const present = new Set(feeds.value.map((f) => FEED_CATEGORY[f]));
    if (roleCodes.value.some((code) => ADMIN_ALERT_ROLES.includes(code))) ['lardo', 'risk'].forEach((c) => present.add(c));
    return present.size > 1 ? TABS.filter((t) => present.has(t.value)) : [];
  });
  const unreadCount = computed(() => items.value.filter((i) => !i.read).length);

  function toItem(feed, row) {
    return {
      key: `${feed}-${row.id}`,
      feed,
      id: row.id,
      title: row.student_name || 'Student',
      icon: '',
      createdAt: row.created_at || row.createdAt || null,
      read: isRead(row),
      text: '',
      meta: '',
      recommendation: '',
      badge: '',
      badgeVariant: '',
      ...FEEDS[feed].map(row),
      row,
    };
  }

  async function load() {
    const { userId: id } = ctx();
    if (!id || !feeds.value.length) {
      items.value = [];
      return;
    }
    loading.value = true;
    loadedAt = Date.now();
    try {
      const results = await Promise.allSettled(
        feeds.value.map((feed) => apiRequest('get', feedUrl(feed, yearQuery()))),
      );
      const next = results.flatMap((result, i) =>
        result.status === 'fulfilled' && Array.isArray(result.value)
          ? result.value.map((row) => toItem(feeds.value[i], row))
          : [],
      );
      // Unread first, then newest.
      next.sort(
        (a, b) =>
          Number(a.read) - Number(b.read) ||
          new Date(b.createdAt || 0) - new Date(a.createdAt || 0),
      );
      // A new unread notification about the page that's open: reload it so
      // the change shows without a manual refresh. Skipped on the first load.
      const known = new Set(items.value.map((i) => i.key));
      const isRefresh = loadedOnce;
      loadedOnce = true;
      if (isRefresh && next.some((i) => !i.read && !known.has(i.key) && pathOf(i)?.split('?')[0] === router.currentRoute.value.path)) {
        requestPageRefresh();
      }
      items.value = next;
    } finally {
      loading.value = false;
    }
  }

  // Where an entry leads, or null if nowhere. A page the user's area doesn't
  // have (e.g. Events for a parent) falls back to their home page instead
  // of "page not found".
  function pathOf(item) {
    const target = FEEDS[item.feed].target(item.row);
    if (target === null) return null;
    const path = `${toValue(basePath)}${target}`;
    const resolved = router.resolve(path);
    const missing = !resolved.matched.length || resolved.matched.some((r) => r.path.includes(':pathMatch'));
    return missing ? `${toValue(basePath)}/dashboard` : path;
  }

  // The bell lists every role's notifications, but pages show the active
  // role's view, so an entry may need another of the user's roles: the one
  // it was sent for, or, when it names none and the current role's menu
  // lacks the page, a role whose menu has it. Null = stay in the current one.
  function roleToOpen(item) {
    const target = FEEDS[item.feed].target(item.row);
    const page = String(target || '').split('?')[0];
    const wanted = FEEDS[item.feed].role?.(item.row) || null;
    const active = ctx().roleCode;
    const granted = parseRoleIds(store.state.user?.user?.subModules)
      .map((id) => roles.value.find((r) => Number(r.id) === Number(id)))
      .filter(Boolean);
    if (wanted) {
      if (covers(active, wanted)) return null;
      const match = granted.find((r) => covers(r.code, wanted));
      if (match) return match;
    }
    if (!page || page === '/dashboard') return null;
    if (menuPages(roles.value.find((r) => r.code === active)).includes(page)) return null;
    return granted.find((r) => r.code !== active && menuPages(r).includes(page)) || null;
  }

  // Makes `role` the account's active role (the server checks it was
  // granted). True when it did.
  async function switchRole(role) {
    try {
      const res = await apiRequest('post', `/auth/changeAssignedModule/Role/${store.state.user?.user?.id}`, {
        assignedModuleID: role.id,
      });
      if (Number(res?.status) !== 200) return false;
      localStorage.setItem('AssignedModID', role.id);
      return true;
    } catch {
      return false;
    }
  }

  async function open(item) {
    let marking = null;
    if (!item.read) {
      items.value = items.value.map((i) => (i === item ? { ...i, read: true } : i));
      marking = apiRequest('patch', feedUrl(item.feed, `/${item.id}/read`)).catch(() => {});
    }
    const path = pathOf(item);
    if (path === null) return;
    // In another role: switch, then load the page afresh so the menu and
    // the page come up in that role. (If the switch fails, the page opens
    // in the current role.)
    const role = roleToOpen(item);
    if (role && (await switchRole(role))) {
      await marking;
      window.location.assign(path);
      return;
    }
    // Already on that exact page (query included): the router won't reload
    // it, so ask the page to. A different query, e.g. another child, navigates.
    if (router.currentRoute.value.fullPath === path) requestPageRefresh();
    else router.push(path);
  }

  // Marks everything in the current feeds read (shown at once; reloaded if
  // the server didn't take it).
  async function markAllRead() {
    if (!unreadCount.value) return;
    items.value = items.value.map((i) => (i.read ? i : { ...i, read: true }));
    const results = await Promise.allSettled(
      feeds.value.map((feed) => apiRequest('post', feedUrl(feed, `/read-all${yearQuery()}`))),
    );
    if (results.some((r) => r.status === 'rejected')) load();
  }

  function refreshIfStale() {
    if (document.visibilityState === 'visible' && Date.now() - loadedAt > FOCUS_REFRESH_MS) load();
  }

  watch(() => [toValue(userId), feeds.value.join(), schoolYearId()], load, { immediate: true });
  // One stream per signed-in user; reopened when the user changes.
  let closeStream = null;
  watch(
    () => toValue(userId),
    (id) => {
      closeStream?.();
      closeStream = id ? openLiveStream(load) : null;
    },
    { immediate: true },
  );
  const timer = setInterval(load, POLL_MS);
  document.addEventListener('visibilitychange', refreshIfStale);
  window.addEventListener('focus', refreshIfStale);
  window.addEventListener(REFRESH_EVENT, load);
  onScopeDispose(() => {
    closeStream?.();
    clearInterval(timer);
    document.removeEventListener('visibilitychange', refreshIfStale);
    window.removeEventListener('focus', refreshIfStale);
    window.removeEventListener(REFRESH_EVENT, load);
  });

  return { items, tabs, unreadCount, loading, load, open, markAllRead };
}
